import { DefaultError } from '@tanstack/query-core';
import { UseSuspenseQueryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { fetchIntegrations } from 'api/integrations';
import { Permission, usePermissions } from 'components/permissions';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Integration } from 'types/domain';

interface AggregatedAccountIntegrations {
    integrations: Integration[];
    donorSearch: Integration | undefined;
    doubleDonation: Integration | undefined;
    mailchimp: Integration | undefined;
    bloomerang: Integration | undefined;
    thankview: Integration | undefined;
}

interface Options
    extends Omit<
        UseSuspenseQueryOptions<
            Integration[] | null,
            DefaultError,
            AggregatedAccountIntegrations,
            any
        >,
        'queryKey' | 'select'
    > {
    enabled?: boolean;
}

const useCurrentAccountIntegrations = (options: Options = {}) => {
    const { isPermitted } = usePermissions([Permission.Admin]);
    const { accountId } = useCurrentAccount();
    const { enabled = !!accountId && isPermitted, ...restOptions } = options;

    return useSuspenseQuery({
        queryKey: accountQueryKeys.integrations(accountId),
        queryFn: () => conditionallyFetchOrNull(() => fetchIntegrations(accountId), enabled),
        select: data => (data ? aggregateIntegrations(data) : null),
        ...restOptions,
    });
};

const aggregateIntegrations = (integrations: Integration[]): AggregatedAccountIntegrations => {
    const getValueByName = (name: string) => integrations.find(item => item.name === name);

    return {
        integrations,
        donorSearch: getValueByName('donorsearch'),
        doubleDonation: getValueByName('doublethedonation'),
        mailchimp: getValueByName('mailchimp'),
        bloomerang: getValueByName('bloomerang'),
        thankview: getValueByName('thankview'),
    };
};

export { useCurrentAccountIntegrations };
